import { createFileRoute } from "@tanstack/react-router";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CalendarDaysIcon,
  ExternalLinkIcon,
  FileTextIcon,
  Loader2,
  PencilIcon,
  PresentationIcon,
  SaveIcon,
  XIcon,
} from "lucide-react";

import createAxios from "@/lib/axios";
import { CourseMaterialGeneration } from "./types";
import { getDownloadURL, ref } from "firebase/storage";
import { storage } from "@/lib/firebase";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { toast } from "sonner";
import { usePeriodicRefresh } from "@/hooks/usePeriodicRefresh";

function convertGcsToUrl(gcsUri: string) {
  return gcsUri.replace(/^gs:\/\//, "https://storage.cloud.google.com/");
}
export const Route = createFileRoute("/dashboard/material/$id")({
  component: CourseMaterial,
  loader: async ({ params: { id } }) => {
    try {
      const client = await createAxios();

      const { data } = await client.get<CourseMaterialGeneration>(
        `${import.meta.env.VITE_BACKEND_URL}/presentation/${id}`,
      );
      data.id ??= data._id!["$oid"]!;
      data.createdAt = data.start_time!;

      if (!(data.status === "completed")) {
        return { id: data.id, course: data };
      }

      const pdfUrl = await getDownloadURL(
        ref(storage, convertGcsToUrl(data.pdf_url!)),
      );
      const pptxUrl = await getDownloadURL(
        ref(storage, convertGcsToUrl(data.ppt_url!)),
      );

      data.pdfUrl = pdfUrl;
      data.pptxUrl = pptxUrl;

      return { id: data.id, course: data };
    } catch (error) {
      toast.error("Error fetching presentation:" + error);
      throw error;
    }
  },
});

function parseCustomDate(dateString: string) {
  const formattedIsoString = dateString.replace("IST+0530", "+05:30");

  // Create a new Date object
  const date = new Date(formattedIsoString);

  return date;
}

function CourseMaterial() {
  usePeriodicRefresh(3000);
  const { course } = Route.useLoaderData();
  const navigate = Route.useNavigate();
  return (
    <Card className="flex size-full flex-col gap-2 px-2">
      <CardHeader className="flex flex-row items-start justify-between">
        <CardTitle
          className="text-4xl font-bold"
          title={course.name || course.id || ""}
        >
          <EditablePptName
            id={course.id}
            name={course.name || course.id}
            invalidate={() =>
              navigate({
                to: "/dashboard/material/$id",
                params: { id: course.id },
              })
            }
          />
        </CardTitle>
        <div className="flex items-center gap-1 text-muted">
          <CalendarDaysIcon className="size-4" />
          <p className="text-sm">
            {formatDate(parseCustomDate(course.createdAt || ""))}
          </p>
        </div>
      </CardHeader>
      {!(course.status === "completed") ? (
        <CardDescription className="flex h-3/4 flex-col gap-2 px-6 text-black">
          <div className="flex h-full flex-col items-center justify-center gap-2">
            <Loader2 className="h-12 w-12 animate-spin" />
            <h2 className="text-2xl font-semibold">
              Generating your presentation...
            </h2>
            <p className="text-xs text-muted-foreground">
              This may take a few minutes
            </p>
          </div>
        </CardDescription>
      ) : (
        <>
          <CardDescription className="flex h-3/4 flex-col gap-2 px-6">
            <PDFViewer url={course.pdfUrl!} />
          </CardDescription>
          <CardFooter className="justify-center gap-4">
            <a href={course.pptxUrl} target="_blank">
              <Button variant="secondary" className="flex gap-2 rounded-lg">
                <PresentationIcon className="size-4" />
                <span>Download PPTX</span>
              </Button>
            </a>
            <a href={course.pdfUrl} target="_blank">
              <Button variant="secondary" className="flex gap-2 rounded-lg">
                <FileTextIcon className="size-4" />
                <span>Download PDF</span>
              </Button>
            </a>
          </CardFooter>
        </>
      )}
    </Card>
  );
}

function EditablePptName({
  id,
  name,
  invalidate,
}: {
  id: string;
  name: string;
  invalidate: () => Promise<void>;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [pptName, setPptName] = useState(name);
  const handleSave = async () => {
    const client = await createAxios();
    await client.post(
      import.meta.env.VITE_BACKEND_URL + `/presentation/${id}/name`,
      { name: pptName },
    );
    await invalidate();
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex gap-2">
        <Input
          className="w-72 bg-neutral-100"
          type="text"
          value={pptName || ""}
          onChange={(e) => setPptName(e.target.value)}
          autoFocus
        />
        <Button
          variant={"outlinecard"}
          className="flex gap-2"
          onClick={handleSave}
        >
          <SaveIcon /> <span>Save</span>
        </Button>
        <Button
          variant={"outlinecard"}
          className="flex gap-2"
          onClick={() => setIsEditing(false)}
        >
          <XIcon /> <span>Cancel</span>
        </Button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <span title={name}>
        {name.length > 30 ? name.slice(0, 30) + "..." : name}
      </span>
      <PencilIcon className="h-6 w-6" onClick={() => setIsEditing(true)} />
    </div>
  );
}

function formatDate(date: Date) {
  const day = date.getDate();
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getFullYear();

  const getOrdinalSuffix = (day: number) => {
    if (day >= 11 && day <= 13) return day + "th";
    switch (day % 10) {
      case 1:
        return day + "st";
      case 2:
        return day + "nd";
      case 3:
        return day + "rd";
      default:
        return day + "th";
    }
  };

  return `${getOrdinalSuffix(day)} ${month} ${year}`;
}

function PDFViewer({ url }: { url: string }) {
  return (
    <object
      data={url}
      type="application/pdf"
      width="100%"
      height="100%"
      className="size-full rounded-lg border-4"
    >
      <div className="flex flex-col items-center gap-2 text-gray-700">
        <p>Error: Unable to display PDF file.</p>
        <a
          href={url}
          target="_blank"
          className="text-blue flex flex-row items-center gap-2 underline"
        >
          <span>Download</span> <ExternalLinkIcon className="size-4" />
        </a>
      </div>
    </object>
  );
}
